import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "theme-uicolor"
    }}>{`@theme-ui/color`}</h1>
    <p>{`Color manipulation utilities for Theme UI`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`npm i @theme-ui/color
`}</code></pre>
    <p>{`Import utilities from the `}<inlineCode parentName="p">{`@theme-ui/color`}</inlineCode>{` package and use them with colors in the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`/** @jsx jsx */
import { jsx } from 'theme-ui'
import { darken, lighten } from '@theme-ui/color'

export default (props) => (
  <div
    {...props}
    sx={{
      color: darken('primary', 0.25),
      bg: lighten('primary', 0.875),
    }}
  />
)
`}</code></pre>
    <h2 {...{
      "id": "api"
    }}>{`API`}</h2>
    <h3 {...{
      "id": "getcolor"
    }}><inlineCode parentName="h3">{`getColor`}</inlineCode></h3>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { getColor } from '@theme-ui/color'
// getColor(theme, 'primary')
`}</code></pre>
    <h3 {...{
      "id": "darken"
    }}><inlineCode parentName="h3">{`darken`}</inlineCode></h3>
    <p>{`Darken a color by an amount 0–1`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { darken } from '@theme-ui/color'
// darken('primary', amount)
`}</code></pre>
    <h3 {...{
      "id": "lighten"
    }}><inlineCode parentName="h3">{`lighten`}</inlineCode></h3>
    <p>{`Lighten a color by an amount 0–1`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { lighten } from '@theme-ui/color'
// lighten('primary', amount)
`}</code></pre>
    <h3 {...{
      "id": "rotate"
    }}><inlineCode parentName="h3">{`rotate`}</inlineCode></h3>
    <p>{`Rotate the hue of a color by an amount 0–360`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { rotate } from '@theme-ui/color'
// rotate('primary', degrees)
`}</code></pre>
    <h3 {...{
      "id": "hue"
    }}><inlineCode parentName="h3">{`hue`}</inlineCode></h3>
    <p>{`Set the hue of a color to a degree 0–360`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { hue } from '@theme-ui/color'
// hue('primary', degrees)
`}</code></pre>
    <h3 {...{
      "id": "saturation"
    }}><inlineCode parentName="h3">{`saturation`}</inlineCode></h3>
    <p>{`Set the saturation of a color to an amount 0–1`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { saturation } from '@theme-ui/color'
// saturation('primary', amount)
`}</code></pre>
    <h3 {...{
      "id": "lightness"
    }}><inlineCode parentName="h3">{`lightness`}</inlineCode></h3>
    <p>{`Set the lightness of a color to an amount 0–1`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { lightness } from '@theme-ui/color'
// lightness('primary', amount)
`}</code></pre>
    <h3 {...{
      "id": "desaturate"
    }}><inlineCode parentName="h3">{`desaturate`}</inlineCode></h3>
    <p>{`Desaturate a color by an amount 0–1`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { desaturate } from '@theme-ui/color'
// desaturate('primary', amount)
`}</code></pre>
    <h3 {...{
      "id": "saturate"
    }}><inlineCode parentName="h3">{`saturate`}</inlineCode></h3>
    <p>{`Saturate a color by an amount 0–1`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { saturate } from '@theme-ui/color'
// saturate('primary', amount)
`}</code></pre>
    <h3 {...{
      "id": "shade"
    }}><inlineCode parentName="h3">{`shade`}</inlineCode></h3>
    <p>{`Shade a color by an amount 0–1`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { shade } from '@theme-ui/color'
// shade('primary', amount)
`}</code></pre>
    <h3 {...{
      "id": "tint"
    }}><inlineCode parentName="h3">{`tint`}</inlineCode></h3>
    <p>{`Tint a color by an amount 0–1`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { tint } from '@theme-ui/color'
// tint('primary', amount)
`}</code></pre>
    <h3 {...{
      "id": "alpha"
    }}><inlineCode parentName="h3">{`alpha`}</inlineCode></h3>
    <p>{`Set the transparency of a color to an amount 0-1`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { alpha } from '@theme-ui/color'
// alpha('primary', amount)
`}</code></pre>
    <h3 {...{
      "id": "transparentize"
    }}><inlineCode parentName="h3">{`transparentize`}</inlineCode></h3>
    <p>{`Similar to `}<inlineCode parentName="p">{`alpha`}</inlineCode>{`, but decreases opacity by the given amount.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { transparentize } from '@theme-ui/color'
// transparentize('primary', amount)
`}</code></pre>
    <h3 {...{
      "id": "mix"
    }}><inlineCode parentName="h3">{`mix`}</inlineCode></h3>
    <p>{`Mix two colors by a specific ratio`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { mix } from '@theme-ui/color'
// mix('primary', 'secondary', ratio)
`}</code></pre>
    <h3 {...{
      "id": "complement"
    }}><inlineCode parentName="h3">{`complement`}</inlineCode></h3>
    <p>{`Get the complement of a color`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { complement } from '@theme-ui/color'
// complement('primary')
`}</code></pre>
    <h3 {...{
      "id": "invert"
    }}><inlineCode parentName="h3">{`invert`}</inlineCode></h3>
    <p>{`Get the inverted color`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { invert } from '@theme-ui/color'
// invert('primary')
`}</code></pre>
    <h3 {...{
      "id": "grayscale"
    }}><inlineCode parentName="h3">{`grayscale`}</inlineCode></h3>
    <p>{`Desaturate the color to grayscale`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { grayscale } from '@theme-ui/color'
// grayscale('primary')
`}</code></pre>
    <h2 {...{
      "id": "advanced-usage"
    }}>{`Advanced Usage`}</h2>
    <p>{`If you want to do something more complex, such as setting up gradients, you can do that with some extra workarounds.`}</p>
    <p>{`We can take the result of any of the above helper functions (which return a function) and call it with the theme object to generate a string in place. This is useful for interpolating values into complex CSS declarations:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<MyComponentWithBackground
  sx={{
    backgroundImage: (t) => \`
      linear-gradient(
        to bottom,
        \${alpha('primary', 0.5)(t)},
        \${alpha('secondary', 0.5)(t)}
      )
    \`,
  }}
/>
`}</code></pre>
    <h2 {...{
      "id": "related"
    }}>{`Related`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://polished.js.org"
        }}>{`Polished`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      